import React, { useState, useEffect, useMemo } from "react";
import { Calendar, Clock, User, MapPin, Phone, Video, Plus, Search, Filter, Download, Eye, Edit, Trash2, X, Check, AlertCircle } from "lucide-react";
import { jobsEndpoint } from "../constants";
import { profilesEndpoint } from "../constants";


// Mock API functions - replace with your actual API calls
const API_BASE = jobsEndpoint;
const token = localStorage.getItem('token');

const apiRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${token}`,
      ...options.headers,
    },
    ...options,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'API request failed');
  }
  console.log(response.data)
  return response.json();
};

// Interview API functions
const interviewAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`my-interviews/?${query}`);
  },
  get: (id) => apiRequest(`interviews/${id}/`),
  update: (id, data) => apiRequest(`interviews/${id}/reschedule/`, { method: 'PATCH', body: JSON.stringify(data) }),
  cancel: (id, reason) => apiRequest(`interviews/${id}/cancel/`, { method: 'PATCH', body: JSON.stringify({ reason }) }),
  complete: (id, data) => apiRequest(`interviews/${id}/complete/`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => apiRequest(`interviews/${id}/`, { method: 'DELETE' }),
  getUserInterviews: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`interviews/user/?${query}`);
  }
};

const InterviewManagement = () => {
  const [currentView, setCurrentView] = useState('list');
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInterview, setSelectedInterview] = useState(null);

  // Fetch interviews
  const fetchInterviews = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await interviewAPI.list(params);
      console.log(data)
      setInterviews(data.results || []);
    } catch (err) {
      setError(err.message);
      // Mock data for demonstration
      setInterviews([
        {
          id: 1,
          job: { id: 1, title: "Frontend Developer", company: "Tech Corp" },
          interviewer: { id: 1, first_name: "John", last_name: "Doe", email: "john@example.com" },
          interviewee: { id: 2, first_name: "Jane", last_name: "Smith", email: "jane@example.com" },
          interview_date: "2024-06-15T10:00:00Z",
          is_active: true,
          is_upcoming: true,
          date_published: "2024-06-10T08:00:00Z"
        },
        {
          id: 2,
          job: { id: 2, title: "Backend Engineer", company: "StartupXYZ" },
          interviewer: { id: 1, first_name: "John", last_name: "Doe", email: "john@example.com" },
          interviewee: { id: 3, first_name: "Bob", last_name: "Wilson", email: "bob@example.com" },
          interview_date: "2024-06-14T14:00:00Z",
          is_active: true,
          is_upcoming: false,
          date_published: "2024-06-08T09:00:00Z"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, []);

  // Filter interviews
  const filteredInterviews = useMemo(() => {
    let result = interviews;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(interview => 
        interview.job?.title?.toLowerCase().includes(search) ||
        interview.interviewer_detail?.first_name?.toLowerCase().includes(search) ||
        interview.interviewer_detail?.last_name?.toLowerCase().includes(search) ||
        interview.interviewee_detail?.first_name?.toLowerCase().includes(search) ||
        interview.interviewee_detail?.last_name?.toLowerCase().includes(search)
      );
    }

    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'upcoming':
          result = result.filter(i => i.is_upcoming && i.is_active);
          break;
        case 'completed':
          result = result.filter(i => !i.is_upcoming && i.is_active);
          break;
        case 'cancelled':
          result = result.filter(i => !i.is_active);
          break;
      }
    }

    return result;
  }, [interviews, searchTerm, statusFilter]);

  const getStatusBadge = (interview) => {
    if (!interview.is_active) {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Cancelled</span>;
    }
    if (interview.is_upcoming) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Upcoming</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Completed</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleString();
  };

  // Schedule Interview Component
  const ScheduleInterview = ({ onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      job: '',
      interviewee: '',  // This will be the applicant ID
      interview_date: '',
      duration_minutes: 60,
      notes: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [googleMeetDetails, setGoogleMeetDetails] = useState(null);
    
    // Data states
    const [jobs, setJobs] = useState([]);
    const [applicants, setApplicants] = useState([]);

    // Load jobs on component mount
    useEffect(() => {
      const fetchJobs = async () => {
        setLoading(true);
        try {
          const jobsResponse = await fetch(jobsEndpoint + "list", {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          if (jobsResponse.ok) {
            const jobsData = await jobsResponse.json();
            setJobs(jobsData.results || jobsData);
          } else {
            console.error('Failed to fetch jobs');
          }
        } catch (error) {
          console.error('Error fetching jobs:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchJobs();
    }, []);

    // Load applicants when job is selected
    useEffect(() => {
      const fetchApplicants = async () => {
        if (!formData.job) {
          setApplicants([]);
          return;
        }

        setLoading(true);
        try {
          const response = await fetch(jobsEndpoint + `${formData.job}/applicant_list`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setApplicants(data.applicants || []);
            setFormData(prev => ({ ...prev, interviewee: '' }));
          } else {
            console.error('Failed to fetch applicants');
            setApplicants([]);
          }
        } catch (error) {
          console.error('Error fetching applicants:', error);
          setApplicants([]);
        } finally {
          setLoading(false);
        }
      };

      fetchApplicants();
    }, [formData.job]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setFormError(null);
      setGoogleMeetDetails(null);

      try {
        // Convert datetime-local to ISO string
        const localDate = new Date(formData.interview_date);
        const isoDate = localDate.toISOString();

        // Prepare the payload for the new endpoint
        const payload = {
          interviewee: formData.interviewee,  // Applicant ID
          interview_date: isoDate,
          duration_minutes: parseInt(formData.duration_minutes, 10),
          notes: formData.notes,
          timezone: formData.timezone,
          is_upcoming: true
        };

        console.log('Sending payload:', payload);

        // Call the new endpoint: /<job_id>/interview_create/
        const response = await fetch(`${jobsEndpoint}${formData.job}/interview_create/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create interview');
        }

        const data = await response.json();
        console.log('Interview created:', data);

        // Store Google Meet details if available
        if (data.google_meet_link) {
          setGoogleMeetDetails({
            meet_link: data.google_meet_link,
            meeting_code: data.meeting_code
          });
        }

        // Show success message
        alert('Interview scheduled successfully!');
        onSuccess();
        
        // Keep modal open if Google Meet details exist, otherwise close
        if (!data.google_meet_link) {
          onClose();
        }
      } catch (err) {
        console.error('Error creating interview:', err);
        setFormError(err.message);
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Schedule Interview</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>

          {formError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {formError}
            </div>
          )}

          {googleMeetDetails && (
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              <p className="font-semibold mb-2">Google Meet Created Successfully!</p>
              <div className="space-y-2">
                <div>
                  <span className="font-medium">Meeting Link:</span>
                  <a 
                    href={googleMeetDetails.meet_link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-700 underline block break-all"
                  >
                    {googleMeetDetails.meet_link}
                  </a>
                </div>
                {googleMeetDetails.meeting_code && (
                  <div>
                    <span className="font-medium">Meeting Code:</span> {googleMeetDetails.meeting_code}
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 w-full"
              >
                Close
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Position <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.job}
                onChange={(e) => setFormData({...formData, job: e.target.value})}
                required
                disabled={loading}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select a job</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>
                    {job.title} - {job.company}
                  </option>
                ))}
              </select>
              {loading && jobs.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">Loading jobs...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interviewee (Applicant) <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.interviewee}
                onChange={(e) => setFormData({...formData, interviewee: e.target.value})}
                required
                disabled={!formData.job || loading}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">
                  {!formData.job 
                    ? "Select a job first" 
                    : applicants.length === 0 
                      ? "No applicants found" 
                      : "Select applicant"}
                </option>
                {applicants.map(applicant => (
                  <option key={applicant.id} value={applicant.id}>
                    Applicant #{applicant.id}
                    {applicant.user_email && ` - ${applicant.user_email}`}
                    {applicant.job_title && ` (${applicant.job_title})`}
                    {applicant.application_status && ` - ${applicant.application_status}`}
                  </option>
                ))}
              </select>
              {loading && formData.job && (
                <p className="text-sm text-gray-500 mt-1">Loading applicants...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interview Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.interview_date}
                onChange={(e) => setFormData({...formData, interview_date: e.target.value})}
                required
                min={new Date().toISOString().slice(0, 16)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (minutes) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                required
                min="15"
                step="15"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.timezone}
                onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., America/New_York"
              />
              <p className="text-xs text-gray-500 mt-1">
                Detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add any additional notes for the interview..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Scheduling...' : 'Schedule Interview'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Interview Details Component
  const InterviewDetails = ({ interview, onClose, onUpdate }) => {
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
      interviewer: interview.interviewer_detail?.pk || '',
      interviewee: interview.interviewee_detail?.pk || '',
      interview_date: interview.interview_date || '',
      notes: interview.notes || ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    const handleUpdate = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setFormError(null);

      try {
        await interviewAPI.update(interview.id, formData);
        onUpdate();
        setEditing(false);
      } catch (err) {
        setFormError(err.message);
      } finally {
        setSubmitting(false);
      }
    };

    const handleCancel = async () => {
      const reason = prompt('Please provide a reason for cancellation:');
      if (reason) {
        try {
          await interviewAPI.cancel(interview.id, reason);
          onUpdate();
        } catch (err) {
          alert('Failed to cancel interview: ' + err.message);
        }
      }
    };

    const handleComplete = async () => {
      const feedback = prompt('Please provide feedback (optional):');
      try {
        await interviewAPI.complete(interview.id, { feedback });
        onUpdate();
      } catch (err) {
        alert('Failed to mark as completed: ' + err.message);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Interview Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>

          {formError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {formError}
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">{interview.job?.title}</h3>
              {getStatusBadge(interview)}
            </div>

            {editing ? (
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interview Date & Time</label>
                  <input
                    type="datetime-local"
                    value={formData.interview_date}
                    onChange={(e) => setFormData({...formData, interview_date: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={3}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? 'Updating...' : 'Update Interview'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Interviewer</label>
                    <p className="text-sm text-gray-900">
                      {interview.interviewer_detail ? 
                        `${interview.interviewer_detail.first_name} ${interview.interviewer_detail.last_name}` : 
                        'Not assigned'
                      }
                    </p>
                    <p className="text-xs text-gray-500">{interview.interviewer?.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Interviewee</label>
                    <p className="text-sm text-gray-900">
                      {interview.interviewee_detail ? 
                        `${interview.interviewee_detail.first_name} ${interview.interviewee_detail.last_name}` : 
                        'Not assigned'
                      }
                    </p>
                    <p className="text-xs text-gray-500">{interview.interviewee?.email}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Scheduled Date & Time</label>
                  <p className="text-sm text-gray-900">{formatDate(interview.interview_date)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Job Position</label>
                  <p className="text-sm text-gray-900">{interview.job_detail?.title} - {interview.job_detail?.company}</p>
                </div>

                {interview.google_meet_link && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Google Meet Link</label>
                    <a 
                      href={interview.google_meet_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                    >
                      {interview.google_meet_link}
                    </a>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-500">Created</label>
                  <p className="text-sm text-gray-900">{formatDate(interview.date_published)}</p>
                </div>

                {interview.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Notes</label>
                    <p className="text-sm text-gray-900">{interview.notes}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  {interview.is_active && (
                    <>
                      <button
                        onClick={() => setEditing(true)}
                        className="px-4 py-2 text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200"
                      >
                        <Edit size={16} className="inline mr-1" />
                        Edit
                      </button>
                      
                      {interview.is_upcoming && (
                        <button
                          onClick={handleComplete}
                          className="px-4 py-2 text-green-600 bg-green-100 rounded-md hover:bg-green-200"
                        >
                          <Check size={16} className="inline mr-1" />
                          Mark Complete
                        </button>
                      )}
                      
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-red-600 bg-red-100 rounded-md hover:bg-red-200"
                      >
                        <X size={16} className="inline mr-1" />
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Interview Management</h1>
            <p className="text-gray-600">Schedule and manage interviews for job positions</p>
          </div>
          <button
            onClick={() => setCurrentView('schedule')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={20} />
            Schedule Interview
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search interviews..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
          <AlertCircle size={20} className="mr-2" />
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading interviews...</p>
        </div>
      )}

      {/* Interviews Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Interviews ({filteredInterviews.length})
            </h2>
          </div>

          {filteredInterviews.length === 0 ? (
            <div className="text-center py-12">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No interviews found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Get started by scheduling your first interview'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <button
                  onClick={() => setCurrentView('schedule')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Schedule Interview
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interviewer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interviewee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInterviews.map((interview) => (
                    <tr key={interview.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {interview.job_detail?.title || 'No job assigned'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {interview.job_detail?.company}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                              <User size={16} className="text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {interview.interviewer_detail ? 
                                `${interview.interviewer_detail.first_name} ${interview.interviewer_detail.last_name}` : 
                                'Not assigned'
                              }
                            </div>
                            <div className="text-sm text-gray-500">
                              {interview.interviewer_detail?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User size={16} className="text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {interview.interviewee_detail ? 
                                `${interview.interviewee_detail.first_name} ${interview.interviewee_detail.last_name}` : 
                                'Not assigned'
                              }
                            </div>
                            <div className="text-sm text-gray-500">
                              {interview.interviewee_detail?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Clock size={16} className="mr-2 text-gray-400" />
                          {formatDate(interview.interview_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(interview)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => setSelectedInterview(interview)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Eye size={16} className="inline mr-1" />
                          View
                        </button>
                        
                        {interview.is_active && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedInterview(interview);
                                setCurrentView('edit');
                              }}
                              className="text-green-600 hover:text-green-900 mr-4"
                            >
                              <Edit size={16} className="inline mr-1" />
                              Edit
                            </button>
                            
                            <button
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to cancel this interview?')) {
                                  const reason = prompt('Please provide a reason for cancellation:');
                                  if (reason) {
                                    try {
                                      await interviewAPI.cancel(interview.id, reason);
                                      fetchInterviews();
                                    } catch (err) {
                                      setError('Failed to cancel interview: ' + err.message);
                                    }
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 size={16} className="inline mr-1" />
                              Cancel
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Components */}
      {currentView === 'schedule' && (
        <ScheduleInterview
          onClose={() => setCurrentView('list')}
          onSuccess={() => {
            fetchInterviews();
            setCurrentView('list');
          }}
        />
      )}

      {selectedInterview && currentView === 'list' && (
        <InterviewDetails
          interview={selectedInterview}
          onClose={() => setSelectedInterview(null)}
          onUpdate={() => {
            fetchInterviews();
            setSelectedInterview(null);
          }}
        />
      )}
    </div>
  );
};

export default InterviewManagement;